import React, { useEffect, useState } from 'react';
import styles from './index.module.scss';
import { useScrollTrigger } from '@/hooks/useScrollTrigger';

// 第五屏：轮播
export default function Screen5() {
  const rootRef = useScrollTrigger({
    triggerClass: '',
    triggerRatio: 0.5,
    scrollContainerSelector: '.aixue-home-scroll',
    enableResizeDetection: true,
    scrollTriggerCallback: () => {
      setIsTriggered(true);
    }
  });
  
  const [slide, setSlide] = useState(0); // 0,1,2 三页
  const [isAnimating, setIsAnimating] = useState(false);
  const [prevSlide, setPrevSlide] = useState(0);
  const [isTriggered, setIsTriggered] = useState(false);

  const handleDotClick = (n: number) => {
    if (isAnimating || n === slide) return;
    setPrevSlide(slide);
    setSlide(n);
    setIsAnimating(true);
  };

  const handlePrev = () => {
    if (isAnimating || slide === 0) return;
    const newSlide = slide - 1;
    setPrevSlide(slide);
    setSlide(newSlide);
    setIsAnimating(true);
  };

  const handleNext = () => {
    if (isAnimating || slide === 2) return;
    const newSlide = slide + 1;
    setPrevSlide(slide);
    setSlide(newSlide);
    setIsAnimating(true);
  };

  // 按钮禁用状态
  const isPrevDisabled = isAnimating || slide === 0;
  const isNextDisabled = isAnimating || slide === 2;

  // 动画结束后清理状态
  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 1000); // 1s 淡入淡出动画时长
      return () => clearTimeout(timer);
    }
  }, [isAnimating]);



  return (
    <section
      className={styles['screen5-cover']}
      ref={rootRef}
      aria-label="核心技术展示"
      itemScope
      itemType="https://schema.org/TechArticle"
    >
      <div className={styles['screen5-swiper']} role="region" aria-label="技术轮播">
        {/* 轮播1 - 爱学大模型 */}
        <article
          className={`${styles['screen5-slide']} ${styles['screen5-slide-1']} ${
            slide === 0
              ? isAnimating && prevSlide === 1
                ? `${styles['screen5-slide--center']} ${styles['screen5-fade-in']}`
                : isAnimating && prevSlide === 2
                  ? `${styles['screen5-slide--center']} ${styles['screen5-fade-in']}`
                  : isTriggered
                    ? `${styles['screen5-slide--center']} ${styles['screen5-fade-in']}`
                    : styles['screen5-slide--center']
              : (slide === 1 || slide === 2) && isAnimating && prevSlide === 0
                ? styles['screen5-fade-out']
                : styles['screen5-slide--left']
          }`}
          aria-hidden={slide !== 0}
          itemScope
          itemType="https://schema.org/SoftwareApplication"
        >
          <div className={styles['screen5-content']}>
            <div className={styles['screen5-title']} itemProp="name">
              爱学大模型
            </div>
            <div className={styles['screen5-subtitle']} itemProp="description">
              <strong className="font-normal">国内领先的“音视频实时对话式AI教育大模型”</strong>
            </div>

            <div className={styles['screen5-desc']} itemProp="featureList">
              <div className={styles['screen5-desc-btm']}>
                在头部的国产大语言模型基础上基于教学数据进行后训练以及强化学习，训练出具备完全实时教学对话能力的独家教育大模型。
                <span className="visibility-hidden opacity-0">
                  <strong>AI一对一教学</strong>、<strong>实时对话</strong>、
                  <strong>个性化学习</strong>。
                </span>
              </div>
              <div>
                基于上述“实时对话式AI教育大模型”，已实现“实时个性化”全链路AI学习产品，是行业里首个（且是唯一一个）能够支持万人以上一起学习、实时反馈的AI产品。
              </div>
            </div>
            <meta itemProp="applicationCategory" content="教育软件" />
            <meta itemProp="operatingSystem" content="Web, iOS, Android" />
          </div>
        </article>

        {/* 轮播2 - 爱学数字人 */}
        <article
          className={`${styles['screen5-slide']} ${styles['screen5-slide-2']} ${
            slide === 1
              ? isAnimating && prevSlide === 0
                ? `${styles['screen5-slide--center']} ${styles['screen5-fade-in']}`
                : isAnimating && prevSlide === 2
                  ? `${styles['screen5-slide--center']} ${styles['screen5-fade-in']}`
                  : isTriggered
                    ? `${styles['screen5-slide--center']} ${styles['screen5-fade-in']}`
                    : styles['screen5-slide--center']
              : slide === 2 && isAnimating && prevSlide === 1
                ? styles['screen5-fade-out']
                : slide === 0 && isAnimating && prevSlide === 1
                  ? styles['screen5-fade-out']
                  : slide < 1
                    ? styles['screen5-slide--right']
                    : styles['screen5-slide--left']
          }`}
          aria-hidden={slide !== 1}
          itemScope
          itemType="https://schema.org/Product"
        >
          <div className={styles['screen5-content']}>
            <div className={styles['screen5-title']} itemProp="name">
              爱学数字人
              <strong className="visibility-hidden opacity-0"> - AI虚拟教师</strong>
            </div>
            <div className={styles['screen5-subtitle']} itemProp="description">
              多场景沉浸交互专家
              <strong className="visibility-hidden opacity-0"> - 3D数字人技术</strong>
            </div>

            <div className={styles['screen5-desc']} itemProp="featureList">
              <div className={styles['screen5-desc-btm']}>
                我们专注于<strong>AI数字人技术</strong>的创新，现已构建覆盖
                <strong>秒级数字人</strong>、<strong>3D高斯数字人</strong>与
                <strong>大模型数字人</strong>
                的全场景技术体系，打造出真人级、可扩展、可商用的数字人系统，在教学互动、内容创作、虚拟交互等场景成功落地。
              </div>
              <div>
                数字人具备多模态感知能力：融合表情、情绪、姿态识别，实现情感化智能响应。
                <strong className="visibility-hidden opacity-0">提供真人级AI教师体验</strong>
              </div>
            </div>
            <meta itemProp="brand" content="爱学教育" />
            <meta itemProp="category" content="教育科技" />
          </div>
        </article>

        {/* 轮播3 - 爱学智能语音交互 */}
        <article
          className={`${styles['screen5-slide']} ${styles['screen5-slide-3']} ${
            slide === 2
              ? isAnimating && prevSlide === 0
                ? `${styles['screen5-slide--center']} ${styles['screen5-fade-in']}`
                : isAnimating && prevSlide === 1
                  ? `${styles['screen5-slide--center']} ${styles['screen5-fade-in']}`
                  : isTriggered
                    ? `${styles['screen5-slide--center']} ${styles['screen5-fade-in']}`
                    : styles['screen5-slide--center']
              : (slide === 0 || slide === 1) && isAnimating && prevSlide === 2
                ? styles['screen5-fade-out']
                : styles['screen5-slide--right']
          }`}
          aria-hidden={slide !== 2}
          itemScope
          itemType="https://schema.org/Product"
        >
          <div className={styles['screen5-content']}>
            <div className={styles['screen5-title']} itemProp="name">
              爱学智能语音交互
              <strong className="visibility-hidden opacity-0"> - AI语音识别</strong>
            </div>
            <p className={styles['screen5-subtitle']} itemProp="description">
              极致的拟真教学沟通（超拟真·强理解·零障碍）
            </p>

            <div className={styles['screen5-desc']} itemProp="featureList">
              <p className={styles['screen5-desc-btm']}>
                <strong>拟人化多情感TTS</strong>：根据学习表现，切换鼓励或指导语气
                <strong className="visibility-hidden opacity-0">提供情感化AI语音体验</strong>
              </p>
              <p className={styles['screen5-desc-btm']}>
                <strong>纯净沟通体验</strong>：声纹过滤杂音，精准判断回答
                <strong className="visibility-hidden opacity-0">支持实时语音识别</strong>
              </p>
              <p>
                <strong>跨语言理解</strong>：中文/英文/方言混合识别
                <strong className="visibility-hidden opacity-0">多语言AI教学支持</strong>
              </p>
            </div>
            <meta itemProp="brand" content="爱学教育" />
            <meta itemProp="category" content="语音识别技术" />
          </div>
        </article>
      </div>

      {/* 左右切换按钮 */}
      <nav className={styles['screen5-nav']} aria-label="轮播导航">
        <button
          className={`${styles['screen5-nav-btn']} ${isPrevDisabled ? styles['screen5-nav-btn--disabled'] : ''}`}
          onClick={handlePrev}
          disabled={isPrevDisabled}
          aria-label="上一项：查看前一个核心技术"
          title="上一项"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          className={`${styles['screen5-nav-btn']} ${isNextDisabled ? styles['screen5-nav-btn--disabled'] : ''}`}
          onClick={handleNext}
          disabled={isNextDisabled}
          aria-label="下一项：查看下一个核心技术"
          title="下一项"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M9 18L15 12L9 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </nav>

      {/* 统一分页器（可点击切换） */}
      <div className={styles['screen5-pagination']} role="tablist" aria-label="技术选择">
        <div className={styles['screen5-pagination-row']}>
          <span
            className={slide === 0 ? styles['screen5-bar'] : styles['screen5-dot']}
            onClick={() => handleDotClick(0)}
          />
          <span
            className={slide === 1 ? styles['screen5-bar'] : styles['screen5-dot']}
            onClick={() => handleDotClick(1)}
          />
          <span
            className={slide === 2 ? styles['screen5-bar'] : styles['screen5-dot']}
            onClick={() => handleDotClick(2)}
          />
        </div>
      </div>
    </section>
  );
}
